export interface PreviewPage {
  key: string;
  label: string;
  group: "Top-level" | "Platform" | "Solutions" | "Perspectives" | "Research";
  path: string;
  tab?: string;
}

export const PREVIEW_SOLUTIONS_FOR_PAGES: { slug: string; name: string }[] = [
  { slug: "actual-intelligence", name: "Actual Intelligence" },
  { slug: "system-bridge", name: "Systems Bridge" },
  { slug: "automation-labs", name: "Managed Automation" },
  { slug: "client-intelligence", name: "Client Intelligence" },
  { slug: "vendor-ops", name: "Vendor Management" },
  { slug: "outage-hub", name: "Outage Monitoring" },
  { slug: "tech-mentor", name: "Tech Mentoring" },
  { slug: "troubleshooting", name: "Troubleshooting" },
  { slug: "lifecycle-navigator", name: "Lifecycle Manager" },
  { slug: "onboarding", name: "Onboarding Navigator" },
  { slug: "project-execution", name: "Project Execution" },
];

export const PERSPECTIVE_TABS: { id: string; label: string }[] = [
  { id: "briefing", label: "Briefing" },
  { id: "ceo", label: "CEO" },
  { id: "coo", label: "COO" },
  { id: "cto", label: "CTO" },
  { id: "cio", label: "CIO" },
  { id: "service", label: "Service" },
  { id: "technician", label: "Technician" },
];

export const PREVIEW_PAGES: PreviewPage[] = [
  { key: "home", label: "Home", group: "Top-level", path: "/" },
  { key: "desktop", label: "Desktop App", group: "Top-level", path: "/desktop" },
  { key: "integrations", label: "Integrations", group: "Top-level", path: "/integrations" },
  { key: "roadmap", label: "Roadmap", group: "Top-level", path: "/roadmap" },
  { key: "feedback", label: "Feedback", group: "Top-level", path: "/feedback" },

  { key: "platform.walkthrough", label: "Platform: Walkthrough", group: "Platform", path: "/walkthrough" },
  { key: "platform.system-map", label: "Platform: System Map", group: "Platform", path: "/system-map" },
  { key: "platform.architecture", label: "Platform: Architecture", group: "Platform", path: "/portal" },

  ...PREVIEW_SOLUTIONS_FOR_PAGES.map<PreviewPage>((s) => ({
    key: `solution.${s.slug}`,
    label: `Solution: ${s.name}`,
    group: "Solutions",
    path: `/solutions/${s.slug}`,
  })),

  ...PERSPECTIVE_TABS.map<PreviewPage>((t) => ({
    key: `perspectives.${t.id}`,
    label: `Perspectives: ${t.label}`,
    group: "Perspectives",
    path: "/perspectives",
    tab: t.id,
  })),

  { key: "research.how-it-works", label: "Research: How It Works", group: "Research", path: "/research/how-it-works" },
  { key: "research.participation", label: "Research: Participation", group: "Research", path: "/research/participation" },
  { key: "research.sweepstakes", label: "Research: Incentives", group: "Research", path: "/sweepstakes" },
  { key: "research.investments", label: "Research: Investor Access", group: "Research", path: "/investments" },
];

export const PREVIEW_PAGE_KEYS = new Set<string>(PREVIEW_PAGES.map((p) => p.key));

export function getPageByKey(key: string): PreviewPage | undefined {
  return PREVIEW_PAGES.find((p) => p.key === key);
}

/**
 * Resolve a navigation event (path + optional ?role= for /perspectives) into the
 * page key it represents. Returns null if no page in the registry matches.
 */
export function resolvePageKey(pathname: string, search?: string): string | null {
  const params = new URLSearchParams(search ?? "");

  if (pathname === "/perspectives") {
    const role = params.get("role") ?? "briefing";
    const match = PREVIEW_PAGES.find((p) => p.path === "/perspectives" && p.tab === role);
    return match?.key ?? null;
  }

  const exact = PREVIEW_PAGES.find((p) => p.path === pathname && p.path !== "/perspectives");
  return exact?.key ?? null;
}

export function isPathAllowed(pathname: string, search: string | undefined, allowedPages: string[]): boolean {
  const allowed = new Set(allowedPages);

  if (pathname === "/perspectives") {
    const role = new URLSearchParams(search ?? "").get("role");
    if (!role) {
      return PERSPECTIVE_TABS.some((t) => allowed.has(`perspectives.${t.id}`));
    }
    return allowed.has(`perspectives.${role}`);
  }

  const page = PREVIEW_PAGES.find((p) => p.path === pathname && p.path !== "/perspectives");
  if (!page) return false;
  return allowed.has(page.key);
}

export function firstAllowedPath(allowedPages: string[]): string {
  for (const key of allowedPages) {
    const page = getPageByKey(key);
    if (page) {
      if (page.tab) return `${page.path}?role=${page.tab}`;
      return page.path;
    }
  }
  return "/";
}
